import type { AgentTask, AgentTaskStatus } from '@septima/contract';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { PageHeader } from '@/components/ui';
import { useAgent } from '@/contexts/AgentContext';
import { useRunDetail } from '@/components/agent/RunDetailPanel';
import { useArtefactSummary, useReviewQueue } from '@/lib/use-review-queue';
import { AGENTS, type AgentMeta } from './agent-kanban-meta';
import { TASK_TYPE_LABELS } from './AgentTaskCard';
import ReviewItemCard from './review/ReviewItemCard';
import { useReviewPanel } from './review/ReviewItemPanel';
import ScheduledRunsView from './ScheduledRunsView';
import {
    Sparkles,
    Clock,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    Loader2,
    CircleDot,
    Eye,
    ChevronDown,
    ChevronRight,
    Bot,
    Zap,
    Timer,
    BarChart3,
    Ban,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Kanban columns = statuses ──────────────────────────────────────────────

interface KanbanColumn {
    status: AgentTaskStatus | 'active';
    label: string;
    icon: React.ReactNode;
    emptyLabel: string;
}

const KANBAN_COLUMNS: KanbanColumn[] = [
    {
        status: 'active',
        label: 'In Flight',
        icon: <CircleDot className="w-4 h-4" />,
        emptyLabel: 'No tasks running',
    },
    {
        status: 'review_required',
        label: 'Needs Review',
        icon: <AlertTriangle className="w-4 h-4" />,
        emptyLabel: 'No pending reviews',
    },
    {
        status: 'approved',
        label: 'Approved',
        icon: <CheckCircle2 className="w-4 h-4" />,
        emptyLabel: 'No approved tasks',
    },
    {
        status: 'completed',
        label: 'Completed',
        icon: <CheckCircle2 className="w-4 h-4" />,
        emptyLabel: 'No completed tasks',
    },
    {
        status: 'rejected',
        label: 'Rejected',
        icon: <XCircle className="w-4 h-4" />,
        emptyLabel: 'No rejections',
    },
    {
        status: 'failed',
        label: 'Failed',
        icon: <XCircle className="w-4 h-4" />,
        emptyLabel: 'No failures',
    },
    // Issue #93 — terminal status for a task whose agent has no registered
    // `AgentDefinition` in this environment. Surfaced as its own column so
    // the deficit is visible, not absorbed into Failed.
    {
        status: 'unavailable',
        label: 'Unavailable',
        icon: <Ban className="w-4 h-4" />,
        emptyLabel: 'No unavailable agents',
    },
];

const STATUS_COLORS: Record<string, string> = {
    active: 'var(--color-accent)',
    queued: 'var(--color-text-tertiary)',
    running: 'var(--color-accent)',
    review_required: 'var(--color-rag-amber)',
    approved: 'var(--color-rag-green)',
    completed: 'var(--color-rag-green)',
    rejected: 'var(--color-rag-red)',
    failed: 'var(--color-rag-red)',
    // Muted (not red) — "unavailable" is a missing capability, not a
    // run-time error.
    unavailable: 'var(--color-text-secondary)',
};

// ─── Surface filter ─────────────────────────────────────────────────────────

type SurfaceFilter = 'all' | 'desk' | 'ops' | 'comply' | 'view';

// ─── Top-level board view (Board kanban vs Scheduled runs) ──────────────────

type BoardView = 'board' | 'scheduled';

// ─── Helper: find agent meta for a task ─────────────────────────────────────

function getAgentForTask(task: AgentTask): AgentMeta | undefined {
    return AGENTS.find((a) => a.taskTypes.includes(task.type));
}

function getAgentName(task: AgentTask): string {
    // Try to extract from triggerDetail first: "ATLAS · ..."
    const detail = task.triggerDetail ?? '';
    const match = detail.match(/^([A-Z]{4,9})\s·/);
    if (match) return match[1];
    return getAgentForTask(task)?.code ?? '?';
}

// Human-readable card description. Prefer the human summary carried in a
// templated "CODE · <summary>" triggerDetail; otherwise (an internal trigger
// token like `atlas-classification` / `numonix-poller`, or no detail) fall back
// to the task-type label so the card never surfaces a raw routing string.
function getTaskDetailFallback(task: AgentTask): string {
    const summary = (task.triggerDetail ?? '').match(/^[A-Z]{3,9}\s·\s(.+)$/);
    if (summary) return summary[1];
    return TASK_TYPE_LABELS[task.type] ?? task.type;
}

function formatTime(iso?: string): string {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
}

function formatDate(iso?: string): string {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch {
        return '';
    }
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function AgentKanbanBoard() {
    const { tasks } = useAgent();
    const { openRun } = useRunDetail();
    // The review drawer is provided globally (also opened from the agent
    // activity panel), so its open/close state lives in <ReviewPanelProvider>.
    const { openReview } = useReviewPanel();
    const [boardView, setBoardView] = useState<BoardView>('board');
    const [surfaceFilter, setSurfaceFilter] = useState<SurfaceFilter>('all');
    const [expandedCard, setExpandedCard] = useState<string | null>(null);
    const [focusedItemId, setFocusedItemId] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const focus = searchParams.get('focus');

    // The "Needs Review" column is sourced from the unified review-queue model
    // (the same source the former review-queue page used), not from the task
    // list directly — so artefacts whose job already completed (e.g. ATLAS
    // post-call) and composer drafts still surface for review.
    const { items: reviewItems } = useReviewQueue();

    // Filter tasks by surface
    const filteredTasks = useMemo(() => {
        if (surfaceFilter === 'all') return tasks;
        return tasks.filter((t) => t.frontend === surfaceFilter || t.frontend === 'global');
    }, [tasks, surfaceFilter]);

    const filteredReviewItems = useMemo(() => {
        if (surfaceFilter === 'all') return reviewItems;
        return reviewItems.filter((it) => it.frontend === surfaceFilter);
    }, [reviewItems, surfaceFilter]);

    // Group tasks by kanban column. `review_required` is handled separately
    // (rendered from `filteredReviewItems`), so it is omitted here.
    const columnData = useMemo(() => {
        const grouped: Record<string, AgentTask[]> = {};
        for (const col of KANBAN_COLUMNS) {
            if (col.status === 'review_required') continue;
            if (col.status === 'active') {
                grouped[col.status] = filteredTasks
                    .filter((t) => t.status === 'queued' || t.status === 'running')
                    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
            } else {
                grouped[col.status] = filteredTasks
                    .filter((t) => t.status === col.status)
                    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
            }
        }
        return grouped;
    }, [filteredTasks]);

    // Summary stats
    const stats = useMemo(() => {
        const running = filteredTasks.filter(
            (t) => t.status === 'running' || t.status === 'queued',
        ).length;
        const review = filteredReviewItems.length;
        const done = filteredTasks.filter(
            (t) => t.status === 'approved' || t.status === 'completed',
        ).length;
        const failed = filteredTasks.filter((t) => t.status === 'failed').length;
        const activeAgents = new Set(filteredTasks.map(getAgentName)).size;
        return { running, review, done, failed, activeAgents, total: filteredTasks.length };
    }, [filteredTasks, filteredReviewItems]);

    const toggleCard = useCallback((id: string) => {
        setExpandedCard((prev) => (prev === id ? null : id));
    }, []);

    // `?focus=<itemId>` deep links (from call/draft surfaces) open the matching
    // review item: panel-kinds open the drawer, inline kinds get highlighted +
    // scrolled into view. Waits until the review items have loaded, then clears
    // the param so a later refresh does not re-trigger.
    useEffect(() => {
        if (!focus) return;
        const match = reviewItems.find((it) => it.id === focus);
        if (!match) return;
        setSurfaceFilter(match.frontend === 'global' ? 'all' : (match.frontend as SurfaceFilter));
        setFocusedItemId(match.id);
        if (match.kind !== 'task') openReview(match);
        const handle = window.setTimeout(() => {
            document
                .getElementById(`review-card-${match.id}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 80);
        const next = new URLSearchParams(searchParams);
        next.delete('focus');
        setSearchParams(next, { replace: true });
        return () => window.clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focus, reviewItems]);

    return (
        <div className="flex flex-col h-full">
            <PageHeader title="Agent Workflows" />

            <div className="flex-1 overflow-hidden flex flex-col">
                {/* Stats bar + filters */}
                <div className="px-6 pt-5 pb-4 flex items-center justify-between border-b border-[rgb(var(--color-divider))]">
                    <div className="flex items-center gap-6">
                        <StatPill
                            icon={<CircleDot className="w-3.5 h-3.5" />}
                            label="In Flight"
                            value={stats.running}
                            color="var(--color-accent)"
                        />
                        <StatPill
                            icon={<AlertTriangle className="w-3.5 h-3.5" />}
                            label="Review"
                            value={stats.review}
                            color="var(--color-rag-amber)"
                        />
                        <StatPill
                            icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                            label="Done"
                            value={stats.done}
                            color="var(--color-rag-green)"
                        />
                        {stats.failed > 0 && (
                            <StatPill
                                icon={<XCircle className="w-3.5 h-3.5" />}
                                label="Failed"
                                value={stats.failed}
                                color="var(--color-rag-red)"
                            />
                        )}
                        <div className="w-px h-5 bg-[rgb(var(--color-divider))]" />
                        <StatPill
                            icon={<Bot className="w-3.5 h-3.5" />}
                            label="Agents"
                            value={stats.activeAgents}
                            color="var(--color-text-secondary)"
                        />
                        <StatPill
                            icon={<BarChart3 className="w-3.5 h-3.5" />}
                            label="Total"
                            value={stats.total}
                            color="var(--color-text-secondary)"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Top-level view toggle: kanban board vs scheduled runs */}
                        <div className="flex items-center gap-1 bg-[rgb(var(--color-surface-secondary))] rounded-lg p-1">
                            {(['board', 'scheduled'] as BoardView[]).map((v) => (
                                <button
                                    key={v}
                                    onClick={() => setBoardView(v)}
                                    className={cn(
                                        'px-3 py-1.5 text-annotation font-medium rounded-md transition-all duration-200',
                                        boardView === v
                                            ? 'bg-[rgb(var(--color-surface-primary))] text-[rgb(var(--color-text-primary))] shadow-sm'
                                            : 'text-[rgb(var(--color-text-tertiary))] hover:text-[rgb(var(--color-text-secondary))]',
                                    )}
                                >
                                    {v === 'board' ? 'Board' : 'Scheduled'}
                                </button>
                            ))}
                        </div>

                        {/* Surface filter tabs — only meaningful for the kanban board */}
                        {boardView === 'board' && (
                            <div className="flex items-center gap-1 bg-[rgb(var(--color-surface-secondary))] rounded-lg p-1">
                                {(['all', 'desk', 'ops', 'comply', 'view'] as SurfaceFilter[]).map(
                                    (s) => (
                                        <button
                                            key={s}
                                            onClick={() => setSurfaceFilter(s)}
                                            className={cn(
                                                'px-3 py-1.5 text-annotation font-medium rounded-md transition-all duration-200',
                                                surfaceFilter === s
                                                    ? 'bg-[rgb(var(--color-surface-primary))] text-[rgb(var(--color-text-primary))] shadow-sm'
                                                    : 'text-[rgb(var(--color-text-tertiary))] hover:text-[rgb(var(--color-text-secondary))]',
                                            )}
                                        >
                                            {s === 'all'
                                                ? 'All'
                                                : s.charAt(0).toUpperCase() + s.slice(1)}
                                        </button>
                                    ),
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Scheduled runs view (WS-I) swaps in for the kanban board. */}
                {boardView === 'scheduled' ? (
                    <ScheduledRunsView />
                ) : (
                    <>
                        {/* Kanban board */}
                        <div className="flex-1 overflow-x-auto overflow-y-hidden">
                            <div className="flex gap-4 p-6 h-full min-w-max">
                                {KANBAN_COLUMNS.map((col) => {
                                    const isReview = col.status === 'review_required';
                                    const colTasks = columnData[col.status] ?? [];
                                    const count = isReview
                                        ? filteredReviewItems.length
                                        : colTasks.length;
                                    // Hide empty terminal columns (rejected, failed) to save space
                                    if (
                                        count === 0 &&
                                        (col.status === 'rejected' || col.status === 'failed')
                                    ) {
                                        return null;
                                    }

                                    return (
                                        <div
                                            key={col.status}
                                            className="flex flex-col w-[320px] flex-shrink-0 rounded-xl bg-[rgb(var(--color-surface-secondary)/0.5)] border border-[rgb(var(--color-divider)/0.6)]"
                                        >
                                            {/* Column header */}
                                            <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(var(--color-divider)/0.4)]">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[rgb(var(--color-text-secondary))]">
                                                        {col.icon}
                                                    </span>
                                                    <span className="text-body-medium font-semibold text-[rgb(var(--color-text-primary))]">
                                                        {col.label}
                                                    </span>
                                                </div>
                                                <span
                                                    className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-annotation font-mono font-bold bg-[rgb(var(--status)/0.12)] text-[rgb(var(--status))]"
                                                    style={{
                                                        ['--status' as string]:
                                                            STATUS_COLORS[col.status],
                                                    }}
                                                >
                                                    {count}
                                                </span>
                                            </div>

                                            {/* Cards */}
                                            <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2.5">
                                                {count === 0 ? (
                                                    <div className="flex flex-col items-center justify-center py-10 text-[rgb(var(--color-text-tertiary))]">
                                                        <Clock className="w-8 h-8 mb-2 opacity-30" />
                                                        <span className="text-annotation">
                                                            {col.emptyLabel}
                                                        </span>
                                                    </div>
                                                ) : isReview ? (
                                                    filteredReviewItems.map((item) => (
                                                        <ReviewItemCard
                                                            key={item.id}
                                                            item={item}
                                                            onOpenPanel={openReview}
                                                            highlighted={focusedItemId === item.id}
                                                        />
                                                    ))
                                                ) : (
                                                    colTasks.map((task) => (
                                                        <TaskCard
                                                            key={task.id}
                                                            task={task}
                                                            expanded={expandedCard === task.id}
                                                            onToggle={toggleCard}
                                                            onOpenRun={openRun}
                                                        />
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ─── Stat pill ──────────────────────────────────────────────────────────────

function StatPill({
    icon,
    label,
    value,
    color,
}: {
    icon: React.ReactNode;
    label: string;
    value: number;
    color: string;
}) {
    return (
        <div className="flex items-center gap-1.5" style={{ ['--stat' as string]: color }}>
            <span className="text-[rgb(var(--stat))]">{icon}</span>
            <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">{label}</span>
            <span className="text-body-medium font-mono font-bold text-[rgb(var(--stat))]">
                {value}
            </span>
        </div>
    );
}

// ─── Task card ──────────────────────────────────────────────────────────────

function TaskCard({
    task,
    expanded,
    onToggle,
    onOpenRun,
}: {
    task: AgentTask;
    expanded: boolean;
    onToggle: (id: string) => void;
    onOpenRun: (id: string) => void;
}) {
    // Terminal runs open the focused run-detail panel on click; in-flight runs
    // keep the inline expand so live steps stay visible without a context switch.
    const terminal =
        task.status === 'completed' ||
        task.status === 'approved' ||
        task.status === 'rejected' ||
        task.status === 'failed' ||
        task.status === 'unavailable';
    const handleActivate = () => (terminal ? onOpenRun(task.id) : onToggle(task.id));

    const agentMeta = getAgentForTask(task);
    const agentName = getAgentName(task);
    const agentColor = agentMeta?.color ?? '148, 163, 184';

    // Progress: how many steps done
    const totalSteps = task.steps.length;
    const doneSteps = task.steps.filter((s) => s.status === 'done').length;
    const runningStep = task.steps.find((s) => s.status === 'running');
    const progress = totalSteps > 0 ? doneSteps / totalSteps : 0;

    // Clean trigger detail — prefer the artefact's emitted summary line
    // when present (PRD #65 user story 1) so each card reads as what the
    // agent found, not the templated `Manual · <client>` triggerDetail.
    const artefactSummary = useArtefactSummary(task);
    const detail = artefactSummary ?? getTaskDetailFallback(task);

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleActivate}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleActivate();
                }
            }}
            aria-expanded={terminal ? undefined : expanded}
            style={{ ['--agent' as string]: agentColor }}
            className={cn(
                'group rounded-lg border bg-[rgb(var(--color-surface-primary))] transition-all duration-200 cursor-pointer text-left',
                'hover:shadow-md hover:border-[rgb(var(--color-divider))]',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent))] focus-visible:ring-offset-1 focus-visible:ring-offset-[rgb(var(--color-surface-secondary))]',
                expanded
                    ? 'border-[rgb(var(--color-divider))] shadow-sm'
                    : 'border-[rgb(var(--color-divider)/0.5)]',
            )}
        >
            {/* Card header — agent color stripe */}
            <div className="h-1 rounded-t-lg bg-[rgb(var(--agent))]" />

            <div className="px-3.5 pt-2.5 pb-3">
                {/* Agent name + time */}
                <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-[rgb(var(--agent))]" />
                        <span className="text-annotation font-bold tracking-wider uppercase text-[rgb(var(--agent))]">
                            {agentName}
                        </span>
                    </div>
                    <span className="text-[10px] font-mono text-[rgb(var(--color-text-tertiary))]">
                        {formatTime(task.createdAt)}
                    </span>
                </div>

                {/* Task description */}
                <p className="text-body-medium text-[rgb(var(--color-text-primary))] leading-snug mb-1.5 line-clamp-2">
                    {detail}
                </p>

                {/* Client + date */}
                {task.clientName && (
                    <p className="text-annotation text-[rgb(var(--color-text-secondary))] mb-2">
                        {task.clientName} · {formatDate(task.createdAt)}
                    </p>
                )}

                {/* Progress bar (for running/completed tasks) */}
                {totalSteps > 0 && (
                    <div className="mb-2">
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] text-[rgb(var(--color-text-tertiary))]">
                                {runningStep
                                    ? runningStep.label
                                    : `${doneSteps}/${totalSteps} steps`}
                            </span>
                            <span className="text-[10px] font-mono text-[rgb(var(--color-text-tertiary))]">
                                {Math.round(progress * 100)}%
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[rgb(var(--color-divider)/0.4)] overflow-hidden">
                            <div
                                className={cn(
                                    'h-full rounded-full transition-all duration-500 bg-[rgb(var(--agent))]',
                                    task.status === 'running' && 'animate-pulse',
                                )}
                                style={{ width: `${Math.max(progress * 100, 2)}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* LLM metadata badges */}
                {(task.totalTokens || task.totalLatencyMs) && (
                    <div className="flex items-center gap-2 mb-2">
                        {task.totalTokens != null && task.totalTokens > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-[rgb(var(--color-surface-secondary))] text-[rgb(var(--color-text-tertiary))]">
                                <Zap className="w-2.5 h-2.5" />
                                {task.totalTokens} tok
                            </span>
                        )}
                        {task.totalLatencyMs != null && task.totalLatencyMs > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-[rgb(var(--color-surface-secondary))] text-[rgb(var(--color-text-tertiary))]">
                                <Timer className="w-2.5 h-2.5" />
                                {(task.totalLatencyMs / 1000).toFixed(1)}s
                            </span>
                        )}
                        {task.confidence && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full font-bold bg-[rgb(var(--agent)/0.1)] text-[rgb(var(--agent))]">
                                {Math.round(task.confidence.score * 100)}%
                            </span>
                        )}
                    </div>
                )}

                {/* Terminal runs open the focused run-detail panel; in-flight
                    runs expand inline to show live steps. */}
                {terminal ? (
                    <div
                        aria-hidden="true"
                        className="flex items-center gap-1 text-[10px] text-[rgb(var(--color-text-tertiary))] group-hover:text-[rgb(var(--color-accent))] transition-colors"
                    >
                        <Eye className="w-3 h-3" />
                        View result
                    </div>
                ) : (
                    <>
                        {/* Expand/collapse indicator (whole card is clickable) */}
                        <div
                            aria-hidden="true"
                            className="flex items-center gap-1 text-[10px] text-[rgb(var(--color-text-tertiary))] group-hover:text-[rgb(var(--color-text-secondary))] transition-colors"
                        >
                            {expanded ? (
                                <ChevronDown className="w-3 h-3" />
                            ) : (
                                <ChevronRight className="w-3 h-3" />
                            )}
                            {expanded ? 'Hide steps' : 'Show steps'}
                        </div>

                        {/* Expanded: step list */}
                        {expanded && (
                            <div className="mt-2.5 pt-2.5 border-t border-[rgb(var(--color-divider)/0.3)]">
                                <div className="flex flex-col gap-1.5">
                                    {task.steps.map((step) => (
                                        <div key={step.id} className="flex items-start gap-2">
                                            <StepIcon status={step.status} />
                                            <div className="flex-1 min-w-0">
                                                <span
                                                    className={cn(
                                                        'text-annotation leading-snug',
                                                        step.status === 'done'
                                                            ? 'text-[rgb(var(--color-text-secondary))]'
                                                            : step.status === 'running'
                                                              ? 'text-[rgb(var(--color-text-primary))] font-medium'
                                                              : 'text-[rgb(var(--color-text-tertiary))]',
                                                    )}
                                                >
                                                    {step.label}
                                                </span>
                                                {step.detail && (
                                                    <p className="text-[10px] text-[rgb(var(--color-text-tertiary))] mt-0.5 truncate">
                                                        {step.detail}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* View in panel link */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenRun(task.id);
                                    }}
                                    className="mt-3 flex items-center gap-1 text-annotation text-[rgb(var(--color-accent))] hover:underline"
                                >
                                    <Eye className="w-3 h-3" />
                                    View full detail
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// ─── Step status icon ───────────────────────────────────────────────────────

function StepIcon({ status }: { status: string }) {
    switch (status) {
        case 'done':
            return (
                <CheckCircle2 className="w-3.5 h-3.5 text-[rgb(var(--color-rag-green))] mt-0.5 flex-shrink-0" />
            );
        case 'running':
            return (
                <Loader2 className="w-3.5 h-3.5 text-[rgb(var(--color-accent))] mt-0.5 flex-shrink-0 animate-spin" />
            );
        case 'error':
            return (
                <XCircle className="w-3.5 h-3.5 text-[rgb(var(--color-rag-red))] mt-0.5 flex-shrink-0" />
            );
        default:
            return (
                <div className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 rounded-full border border-[rgb(var(--color-divider))]" />
            );
    }
}
